import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  DatePicker,
  Drawer,
  Input,
  Modal,
  Progress,
  Select,
  Space,
  Table,
  Tag,
  message,
} from "antd";
import {
  ExportOutlined,
  MailOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import dayjsTZ from "../../../../utils/dayjs";
import { API } from "../../../../helpers/API/amplifyApi";
import { employeeRequests as employeeRequestsReducer } from "../../../../store/actions";
import MondayButton from "../../../commonComponents/MondayButton/MondayButton";
import InboxReviewDrawer from "./InboxReviewDrawer";
import {
  filterQueueRows,
  findExactLeaveDuplicate,
  gmailOpenUrl,
} from "./inboxHelpers";
import { useAuth } from "../../../../authentication/authHelpers";
import {
  gmailScopeQuery,
  resolveGmailScopeId,
  withGmailScope,
} from "../../../../utils/gmailScope";

const { RangePicker } = DatePicker;

const STATUS_TAG = {
  Queued: "blue",
  NeedsReview: "orange",
  Converted: "green",
  Dismissed: "default",
};

const InboxQueuePanel = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const auth = useAuth();
  const scopeId = resolveGmailScopeId(auth);
  const { employees } = useSelector((state) => state.employeesList);
  const { employeeRequests } = useSelector((state) => state.employeeRequests);
  const { isDarkMode } = useSelector((state) => state.usersList);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [edit, setEdit] = useState(null);
  const [linking, setLinking] = useState(null);

  const [filterEmail, setFilterEmail] = useState("");
  const [filterDates, setFilterDates] = useState(null);
  const [filterStatus, setFilterStatus] = useState("open");

  const load = useCallback(async () => {
    if (!scopeId) {
      message.error("Mungon dega e menaxhuar për inbox-in");
      return;
    }
    setLoading(true);
    try {
      const res = await API.get(
        "gmailInbox",
        "/gmailInbox/queue",
        gmailScopeQuery(auth)
      );
      setRows(res?.items || res || []);
    } catch (e) {
      message.error("Nuk u ngarkua radha e inbox-it");
    } finally {
      setLoading(false);
    }
  }, [auth, scopeId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await API.post("gmailInbox", "/gmailInbox/sync", {
        body: withGmailScope({}, auth),
        ...gmailScopeQuery(auth),
      });
      message.success(
        `Sinkronizuar: ${res?.fetched ?? 0} email, ${res?.queued ?? 0} në radhë` +
          (res?.skippedDuplicate
            ? `, ${res.skippedDuplicate} dublikatë`
            : "") +
          (res?.linkedExisting ? `, ${res.linkedExisting} të lidhura` : "")
      );
      await load();
    } catch (e) {
      message.error(e?.response?.data?.error || e?.message || "Sync dështoi");
    } finally {
      setSyncing(false);
    }
  };

  const dismiss = async (record) => {
    try {
      await API.put("gmailInbox", `/gmailInbox/queue/${record.queueId}`, {
        body: withGmailScope({ status: "Dismissed" }, auth),
        ...gmailScopeQuery(auth),
      });
      message.success("U hoq nga radha");
      load();
    } catch (e) {
      message.error("Dismiss dështoi");
    }
  };

  const runConvert = async (record, { approve, force } = {}) => {
    const ai = {
      ...(record.ai || {}),
      ...(edit?.queueId === record.queueId ? edit.ai : {}),
    };
    const emp =
      (employees || []).find(
        (e) =>
          e.employeeId === ai?.employeeMatch?.employeeId ||
          e.userSub === ai?.employeeMatch?.userSub
      ) ||
      (employees || []).find((e) => e.employeeId === edit?.employeeId);

    if (!emp?.userSub) {
      message.warning("Zgjidh punonjësin përpara konvertimit");
      return;
    }
    if (!ai?.startDate || !ai?.endDate) {
      message.warning("Mungojnë datat e fillimit/mbarimit");
      return;
    }

    try {
      const res = await API.post("gmailInbox", "/gmailInbox/convert", {
        body: withGmailScope(
          {
            queueId: record.queueId,
            approve: !!approve,
            force: !!force,
            overrides: {
              employeeId: emp.employeeId,
              userSub: emp.userSub,
              given_name: emp.employeeFirstName,
              family_name: emp.employeeLastName,
              employeeDepartmentName: emp.employeeDepartmentName,
              employeeTeam: emp.employeeTeam,
              requestType: ai.requestType || "Pushime",
              startDate: ai.startDate,
              endDate: ai.endDate,
              description: ai.description || record.snippet || "",
            },
          },
          auth
        ),
        ...gmailScopeQuery(auth),
      });

      if (res?.duplicate && res?.linked) {
        message.info(
          "Kërkesë ekzistuese — email-i u lidh (pa dublikatë të re)"
        );
      } else if (res?.request && !res?.duplicate) {
        dispatch(
          employeeRequestsReducer([...(employeeRequests || []), res.request])
        );
        message.success(
          approve ? "U konvertua dhe u aprovua" : "U konvertua si Pending"
        );
      } else {
        message.success("U përditësua radha");
      }
      setEdit(null);
      load();
    } catch (e) {
      const err = e?.response?.data?.error || e?.message || "Konvertimi dështoi";
      if (err === "already_converted") {
        message.warning("Ky email është konvertuar tashmë");
        load();
        return;
      }
      message.error(err);
    }
  };

  const convert = async (record, { approve } = {}) => {
    const ai = {
      ...(record.ai || {}),
      ...(edit?.queueId === record.queueId ? edit.ai : {}),
    };
    const emp =
      (employees || []).find(
        (e) =>
          e.employeeId === ai?.employeeMatch?.employeeId ||
          e.userSub === ai?.employeeMatch?.userSub
      ) ||
      (employees || []).find((e) => e.employeeId === edit?.employeeId);

    const localDup = findExactLeaveDuplicate(employeeRequests, {
      employeeId: emp?.employeeId || ai?.employeeMatch?.employeeId,
      userSub: emp?.userSub || ai?.employeeMatch?.userSub,
      startDate: ai?.startDate,
      endDate: ai?.endDate,
      requestType: ai?.requestType || "Pushime",
    });

    if (localDup) {
      Modal.confirm({
        title: "Kërkesë ekzistuese — parandalim dublikate",
        content: (
          <div>
            <p>
              <strong>
                {localDup.given_name} {localDup.family_name}
              </strong>{" "}
              ka tashmë <strong>{localDup.requestType}</strong> (
              {localDup.requestStatus}) për të njëjtat data.
            </p>
            <p>Email-i do të lidhet me kërkesën ekzistuese (pa krijuar dublikatë).</p>
          </div>
        ),
        okText: "Lidh me ekzistuesen",
        cancelText: "Anulo",
        onOk: () => linkExisting(record, localDup),
      });
      return;
    }

    return runConvert(record, { approve });
  };

  const linkExisting = async (record, request) => {
    setLinking(request.requestId);
    try {
      await API.put("gmailInbox", `/gmailInbox/queue/${record.queueId}`, {
        body: withGmailScope(
          {
            status: "Converted",
            convertedRequestId: request.requestId,
            ai: {
              ...(record.ai || {}),
              linkedToExisting: true,
              employeeMatch: {
                employeeId: request.employeeId,
                userSub: request.userSub,
                name: `${request.given_name || ""} ${
                  request.family_name || ""
                }`.trim(),
                confidence: 1,
              },
              startDate: dayjsTZ(request.requestPeriod[0]).format("YYYY-MM-DD"),
              endDate: dayjsTZ(request.requestPeriod.at(-1)).format(
                "YYYY-MM-DD"
              ),
              requestType: request.requestType || record.ai?.requestType,
            },
          },
          auth
        ),
        ...gmailScopeQuery(auth),
      });
      message.success("Email-i u lidh me kërkesën ekzistuese");
      setEdit(null);
      load();
    } catch (e) {
      message.error(e?.response?.data?.error || e?.message || "Lidhja dështoi");
    } finally {
      setLinking(null);
    }
  };

  const filteredRows = useMemo(() => {
    const status =
      filterStatus === "open"
        ? null
        : filterStatus === "all"
          ? "all"
          : filterStatus;
    let list = filterQueueRows(rows, {
      email: filterEmail,
      dateRange: filterDates,
      status: status === null ? undefined : status,
    });
    if (filterStatus === "open") {
      list = list.filter(
        (r) => r.status === "Queued" || r.status === "NeedsReview"
      );
    }
    return list;
  }, [rows, filterEmail, filterDates, filterStatus]);

  const columns = [
    {
      title: "Marrë",
      dataIndex: "receivedAt",
      width: 118,
      render: (v) => (v ? dayjsTZ(v).format("DD/MM/YY HH:mm") : "—"),
      sorter: (a, b) => (a.receivedAt || 0) - (b.receivedAt || 0),
      defaultSortOrder: "descend",
    },
    {
      title: "Nga",
      dataIndex: "fromEmail",
      ellipsis: true,
      width: 168,
      render: (v) => <span className="inbox-from">{v}</span>,
    },
    {
      title: "Subjekt",
      dataIndex: "subject",
      ellipsis: true,
      render: (v, r) => (
        <button
          type="button"
          className="inbox-subject-btn"
          onClick={() => setEdit({ ...r, ai: { ...r.ai } })}
        >
          {v || "(pa subjekt)"}
        </button>
      ),
    },
    {
      title: "Punonjësi",
      key: "match",
      width: 140,
      render: (_, r) => r.ai?.employeeMatch?.name || "—",
    },
    {
      title: "Datat",
      key: "dates",
      width: 128,
      render: (_, r) =>
        r.ai?.startDate && r.ai?.endDate
          ? `${dayjsTZ(r.ai.startDate).format("DD/MM")}–${dayjsTZ(
              r.ai.endDate
            ).format("DD/MM")}`
          : "—",
    },
    {
      title: "AI",
      key: "conf",
      width: 72,
      render: (_, r) => {
        const c = Math.round((r.ai?.confidence || 0) * 100);
        return (
          <Progress
            percent={c}
            size="small"
            showInfo={false}
            status={c < 60 ? "exception" : "normal"}
          />
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 110,
      render: (s) => <Tag color={STATUS_TAG[s] || "default"}>{s}</Tag>,
    },
    {
      title: "",
      key: "actions",
      width: 200,
      render: (_, record) => {
        const url = gmailOpenUrl(record.threadId, record.gmailMessageId);
        if (record.status === "Converted" || record.status === "Dismissed") {
          return url ? (
            <Button
              size="small"
              type="link"
              icon={<ExportOutlined />}
              href={url}
              target="_blank"
              rel="noreferrer"
            >
              Gmail
            </Button>
          ) : null;
        }
        return (
          <Space size={0} wrap className="inbox-row-actions">
            <Button
              size="small"
              type="link"
              onClick={() => setEdit({ ...record, ai: { ...record.ai } })}
            >
              Hap email
            </Button>
            {url ? (
              <Button
                size="small"
                type="link"
                icon={<ExportOutlined />}
                href={url}
                target="_blank"
                rel="noreferrer"
              />
            ) : null}
            <Button
              size="small"
              type="link"
              danger
              onClick={() => dismiss(record)}
            >
              Hiq
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width="min(1120px, 98vw)"
        title={
          <Space>
            <MailOutlined />
            Inbox — radha e pushimeve
          </Space>
        }
        className={`planer-inbox-drawer ${isDarkMode ? "is-dark" : ""}`}
        extra={
          <MondayButton
            className="mondayButtonBlue"
            Icon={<ReloadOutlined />}
            onClick={syncNow}
            disabled={syncing}
          >
            {syncing ? "Duke sinkronizuar…" : "Sync tani"}
          </MondayButton>
        }
      >
        <div className="inbox-toolbar">
          <Input
            allowClear
            placeholder="Filtro sipas email / subjekti…"
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            className="inbox-toolbar__email"
          />
          <RangePicker
            format="DD/MM/YYYY"
            value={filterDates}
            onChange={setFilterDates}
            placeholder={["Marrë nga", "deri"]}
          />
          <Select
            value={filterStatus}
            onChange={setFilterStatus}
            style={{ width: 150 }}
            options={[
              { value: "open", label: "Të hapura" },
              { value: "NeedsReview", label: "NeedsReview" },
              { value: "Queued", label: "Queued" },
              { value: "Converted", label: "Converted" },
              { value: "all", label: "Të gjitha" },
            ]}
          />
          <span className="inbox-toolbar__count">
            {filteredRows.length} email
          </span>
        </div>

        <Table
          className="inbox-table"
          rowKey="queueId"
          loading={loading}
          dataSource={filteredRows}
          columns={columns}
          size="middle"
          pagination={{ pageSize: 10, showSizeChanger: false }}
          rowClassName={(r) =>
            r.status === "NeedsReview" ? "inbox-row--review" : ""
          }
        />
      </Drawer>

      <InboxReviewDrawer
        active={edit}
        onClose={() => setEdit(null)}
        onChange={setEdit}
        onConvert={convert}
        onLinkExisting={linkExisting}
        linking={linking}
      />
    </>
  );
};

export default InboxQueuePanel;
